import { MouseEventHandler, forwardRef } from "react";

import { useMenuOpen } from "mds/components/MenuOpenContext";
import type { PolymorphicProps, PolymorphicRef } from "types";
import { type TrackingProps, trackEvent } from "utils/amplitude";

export type BaseButtonProps<T extends React.ElementType = "button"> = PolymorphicProps<T> &
  TrackingProps & {
    className?: string;
    isSelected?: boolean;
    onClick?: MouseEventHandler<unknown>;
  };

/**
 * You probably do NOT want to use this!
 * This is an abstract base component that other components inherit from.
 * It's better to use one of the child components, like `SquareButton` or `RoundedButton`.
 *
 * If you are editing this component, do NOT add any styling to it. No CSS classes, no styles, no nothing.
 * The child components are responsible for styling.
 */
export const BaseButton = forwardRef<HTMLButtonElement, Omit<BaseButtonProps<"button">, "ref">>(
  function BaseButtonInternal(
    {
      as,
      className,
      onClick,
      eventName,
      eventProps = {},
      isSelected,
      ...rest
    }: BaseButtonProps<"button">,
    ref,
  ) {
    const Component = as || "button";
    const isMenuOpen = useMenuOpen();
    const selected = isSelected ?? isMenuOpen;

    const clickHandler: MouseEventHandler<unknown> | undefined = eventName
      ? (e) => {
          trackEvent(eventName, { eventCategory: "Button press", ...eventProps });
          if (onClick) {
            onClick(e);
          }
        }
      : onClick;

    // If we are using a button, we need to set the type to "button" to prevent
    // it from submitting a form when the button is clicked.
    const buttonProps = Component === "button" ? ({ type: "button" } as const) : {};

    return (
      <Component
        className={className}
        ref={ref}
        onClick={clickHandler}
        {...buttonProps}
        {...rest}
        data-selected={selected || undefined}
      />
    );
  },
) as <T extends React.ElementType = "button">(
  props: BaseButtonProps<T> & { ref?: PolymorphicRef<T> },
) => React.ReactNode;
